import { INLINES } from '@contentful/rich-text-types';
import cls from 'classnames';
import React, { FC } from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useSelector } from 'react-redux';

import NormalButton from '@/components/button/normal';
import Input from '@/components/form-elements/input';
import RichTextRenderer, {
  renderInlineHyperlink,
} from '@/components/rich-text-renderer';
import getThemedButtonProps from '@/lib/get-themed-button-props';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useVariables from '@/lib/hooks/use-variables';
import { selectGlobalPageTheme } from '@/store/slices/global';
import { NewsletterStaticVariant } from '@/types/views/sections';

import getVariantVariableKey from '../../lib/get-variant-variable-key';
import { NewsletterFormInputData } from '../../lib/newsletter-form-data';
import styles from './styles';

const PHONE_REGEX_PATTERN = /^\+[1-9]\d{1,14}$/;

type FourthStepProps = {
  register: UseFormRegister<NewsletterFormInputData>;
  errors: FieldErrors;
  buttonDisabled: boolean;
  onNextButtonClick: () => void;
  onPrevButtonClick: () => void;
  inputValues: {
    phone: string;
    countryCode: string;
  };
  newsletterVariant?: NewsletterStaticVariant;
};

const COUNTRIES = [
  { code: '+41', label: '🇨🇭 +41' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+43', label: '🇦🇹 +43' },
  { code: '+39', label: '🇮🇹 +39' },
  { code: '+33', label: '🇫🇷 +33' },
];

const PHONE_PLACEHOLDERS: Record<string, string> = {
  '+41': '79 123 45 67',
  '+49': '178 3456789',
  '+43': '664 123456',
  '+39': '312 345 6789',
  '+33': '6 12 34 56 78',
};

const normalizePhoneNumber = (countryCode: string, phone: string) => {
  const normalizedCountryCode = (countryCode || '+41').trim();
  const digitsOnly = (phone || '').replace(/\D/g, '').replace(/^0+/, '');

  return `${normalizedCountryCode}${digitsOnly}`;
};

const FourthStep: FC<FourthStepProps> = ({
  register,
  errors,
  buttonDisabled,
  onPrevButtonClick,
  onNextButtonClick,
  inputValues,
  newsletterVariant = 'newsletter',
}) => {
  const variables = useVariables();
  const pageTheme = useSelector(selectGlobalPageTheme);
  const buttonProps = getThemedButtonProps(pageTheme);
  const key = (suffix: string) =>
    getVariantVariableKey(newsletterVariant, suffix);

  const title = getVariablePlainText(variables[key('enter-phone-text')]);
  const text = variables[key('datenschutz-text')];
  const countryCodeLabel = getVariablePlainText(
    variables[key('country-code-text')],
  );
  const phoneLabel = getVariablePlainText(variables[key('signup-phone-text')]);
  const ctaBtnText = getVariablePlainText(variables[key('text')]);
  const cancelBtnText = getVariablePlainText(variables[key('cancel-text')]);
  const phoneErrorText = getVariablePlainText(variables['phone-error-text']);
  const invalidPhoneText = getVariablePlainText(
    variables['invalid-phone-text'],
  );

  return (
    <div>
      <h2 className={styles.heading}>{title || 'Handynummer'}</h2>

      <RichTextRenderer
        body={text}
        className={styles.text}
        options={{
          renderNode: {
            [INLINES.HYPERLINK]: renderInlineHyperlink(styles.text),
          },
        }}
      />

      <div className={styles.formGroup}>
        <div className={cls(styles.inputWrapper)}>
          <label htmlFor="countryCode" className="sr-only">
            {countryCodeLabel || 'Vorwahl'}
          </label>

          <select
            id="countryCode"
            className={cls(
              styles.countryCodeSelect(!!inputValues.countryCode),
              'placeholder-gray-3',
            )}
            defaultValue="+41"
            {...register('countryCode', {
              required: phoneErrorText || 'Phone number is required',
            })}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.label}
              </option>
            ))}
          </select>
        </div>

        <Input
          {...register('phone', {
            required: phoneErrorText || 'Phone number is required',

            validate: (value: string) => {
              if (value.includes('+')) {
                return 'Please enter the number without country code.';
              }

              if (value.startsWith('00')) {
                return 'Please do not enter an international prefix.';
              }

              return (
                PHONE_REGEX_PATTERN.test(
                  normalizePhoneNumber(inputValues.countryCode, value),
                ) ||
                invalidPhoneText ||
                'Please enter a valid phone number.'
              );
            },
          })}
          id="phone"
          label={phoneLabel || 'Handynummer'}
          type="tel"
          placeholder={
            PHONE_PLACEHOLDERS[inputValues.countryCode] || 'Phone number'
          }
          containerClassName={styles.inputWrapper}
          className={styles.input(!!inputValues.phone)}
          error={errors?.phone?.message}
        />

        <NormalButton
          {...buttonProps}
          text={ctaBtnText}
          size="L"
          className={styles.button}
          onClick={onNextButtonClick}
          disabled={buttonDisabled}
        />
      </div>

      <div className={styles.btnContainer}>
        <button
          type="button"
          onClick={onPrevButtonClick}
          className={styles.cancelBtn}
        >
          {cancelBtnText}
        </button>
      </div>
    </div>
  );
};

export default FourthStep;
